import { useCallback, useState } from "react";
import { API } from "../../../services/api";
import { ADMIN_API_CONFIG } from "../model/administrationConstants";
import { administrationPayload, mergeAdministrationDraft } from "../model/administrationRules";
import { refreshDraft } from "./useResidents";

const samePatient = (a, b) => String(a) === String(b);

// Fiche complète d'un résident ; si l'endpoint détaillé échoue, on la cherche dans la liste complète de l'EHPAD.
async function fetchPatient(patientId, ehpadId) {
    try {
        return (await API.get(`/administration/patients/${patientId}`, ADMIN_API_CONFIG)).data;
    } catch (detailError) {
        if (!ehpadId) throw detailError;
        const all = (await API.get(`/administration/ehpads/${ehpadId}/patients`, ADMIN_API_CONFIG)).data;
        const patient = all.find(row => samePatient(row.patientId, patientId));
        if (!patient) throw detailError;
        return patient;
    }
}

// Fiches chargées et brouillons : chaque fiche rechargée remplace la ligne et rafraîchit son brouillon.
export function useLoadedPatients({ ehpadId, setRows, syncPatient, onError }) {
    const [drafts, setDrafts] = useState({});
    const [detailLoading, setDetailLoading] = useState(false);

    const replaceLoadedPatient = useCallback(patient => {
        setRows(current => (
            current.some(row => samePatient(row.patientId, patient.patientId))
                ? current.map(row => samePatient(row.patientId, patient.patientId) ? patient : row)
                : [...current, patient]
        ));
        setDrafts(current => (current[patient.patientId] ? { ...current, [patient.patientId]: refreshDraft(current[patient.patientId], patient) } : current));
        syncPatient(patient);
    }, [setRows, syncPatient]);

    const loadPatientDetail = useCallback(async patientId => {
        if (!patientId) return null;
        setDetailLoading(true);
        onError("");
        try {
            const patient = await fetchPatient(patientId, ehpadId);
            replaceLoadedPatient(patient);
            return patient;
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible de charger cette fiche résident.");
            return null;
        } finally {
            setDetailLoading(false);
        }
    }, [ehpadId, onError, replaceLoadedPatient]);

    const updateRowDraft = (row, patch) => {
        setDrafts(current => ({
            ...current,
            [row.patientId]: { ...(current[row.patientId] || row), ...patch, _base: current[row.patientId]?._base || row },
        }));
        onError("");
    };

    const cancelRow = patientId => {
        setDrafts(current => {
            const next = { ...current };
            delete next[patientId];
            return next;
        });
    };

    return { drafts, setDrafts, detailLoading, replaceLoadedPatient, loadPatientDetail, updateRowDraft, cancelRow };
}

// Enregistrement d'une fiche. Si un autre utilisateur l'a modifiée entre-temps (409), les champs différents sont
// fusionnés automatiquement et l'utilisateur choisit la valeur à garder pour les champs modifiés des deux côtés.
export function useResidentSaving({ ehpadId, loaded, onError }) {
    const { drafts, replaceLoadedPatient, cancelRow } = loaded;
    const [savingIds, setSavingIds] = useState({});
    const [mergeConflict, setMergeConflict] = useState(null);
    const setSaving = (patientId, saving) => setSavingIds(current => ({ ...current, [patientId]: saving }));

    const putPatient = async (patientId, values, clearConflict = true) => {
        const response = await API.put(`/administration/patients/${patientId}`, administrationPayload(values), ADMIN_API_CONFIG);
        replaceLoadedPatient(response.data);
        cancelRow(patientId);
        if (clearConflict) setMergeConflict(null);
    };

    const saveMergedRow = async (patientId, rowDraft, base) => {
        let latest;
        try {
            latest = (await API.get(`/administration/patients/${patientId}`, ADMIN_API_CONFIG)).data;
        } catch (detailError) {
            latest = (await API.get(`/administration/ehpads/${ehpadId}/patients`, ADMIN_API_CONFIG)).data
                .find(row => samePatient(row.patientId, patientId));
        }
        if (!latest) throw new Error("Cette fiche n’existe plus.");
        const { merged, conflicts } = mergeAdministrationDraft(base, rowDraft, latest);
        replaceLoadedPatient(latest);
        if (conflicts.length > 0) {
            setMergeConflict({
                patientId, local: rowDraft, remote: latest, merged, conflicts,
                choices: Object.fromEntries(conflicts.map(field => [field, "mine"])),
            });
            return;
        }
        await putPatient(patientId, merged);
    };

    const saveWithMerge = async (patientId, values, base, failureMessage, clearConflict) => {
        setSaving(patientId, true);
        onError("");
        try {
            await putPatient(patientId, values, clearConflict);
        } catch (requestError) {
            if (requestError.response?.status !== 409) {
                onError(requestError.response?.data?.message || failureMessage);
                return;
            }
            try {
                await saveMergedRow(patientId, values, base);
            } catch (mergeError) {
                onError(mergeError.response?.data?.message || mergeError.message || "Impossible de fusionner cette fiche.");
            }
        } finally {
            setSaving(patientId, false);
        }
    };

    const saveRow = async patientId => {
        const rowDraft = drafts[patientId];
        if (!rowDraft) return;
        await saveWithMerge(patientId, rowDraft, rowDraft._base || rowDraft, "Impossible d'enregistrer cette ligne.", false);
    };

    const confirmMerge = async () => {
        if (!mergeConflict) return;
        const { patientId, local, remote, merged, conflicts, choices } = mergeConflict;
        const resolved = { ...merged, version: remote.version };
        conflicts.forEach(field => {
            resolved[field] = choices[field] === "mine" ? local[field] : remote[field];
        });
        await saveWithMerge(patientId, resolved, remote, "Impossible d’enregistrer la fusion.", true);
    };

    return { savingIds, setSaving, mergeConflict, setMergeConflict, saveRow, confirmMerge };
}
